<template>
    <div id="about">
        <!-- HERO -->
        <section class="about-hero">
            <div class="hero-media">
                <OptimizedImage
                    :webp="heroWebp"
                    :avif="heroAvif"
                    alt=""
                    img-class="hero-image"
                    loading="eager"
                    fill
                />
                <div class="hero-grain"></div>
                <div class="hero-overlay"></div>
            </div>

            <div class="hero-top">
                <span class="hero-brand">AGH SOLAR BOAT</span>
                <span class="hero-divider"></span>
                <span class="hero-tag">{{ $t('about.page.tag') }}</span>
                <span class="hero-divider hero-divider--hide"></span>
                <span class="hero-since hero-since--hide">{{ $t('about.page.since') }}</span>
            </div>

            <div class="hero-content">
                <span class="hero-eyebrow">{{ $t('about.page.eyebrow') }}</span>
                <h1 class="hero-title" v-html="$t('about.header.line_1')" />
                <p class="hero-lead">{{ $t('about.header.line_2') }}</p>
            </div>

            <div class="hero-bottom">
                <a href="#history" class="scroll-link">
                    <span>{{ $t('about.page.scroll') }}</span>
                    <span class="scroll-arrow">↓</span>
                </a>
            </div>
        </section>

        <!-- HISTORY -->
        <section id="history" class="block-light">
            <v-container class="block-container">
                <div class="section-meta">
                    <span class="chapter-num">01</span>
                    <span class="chapter-divider"></span>
                    <span class="chapter-eyebrow">{{ $t('about.page.history_eyebrow') }}</span>
                </div>

                <div class="section-grid">
                    <div class="section-headline">
                        <h2 class="section-title">{{ $t('about.history.title') }}.</h2>
                    </div>
                    <div class="section-body">
                        <p class="section-text">{{ $t('about.history.description') }}</p>
                        <a href="#beginning" class="ghost-cta">
                            <span>{{ $t('about.history.meet-us') }}</span>
                            <span class="ghost-cta-arrow">↓</span>
                        </a>
                    </div>
                </div>
            </v-container>
        </section>

        <!-- PARALLAX BREAK -->
        <section class="parallax-break">
            <div class="parallax-image" :style="{ backgroundImage: `url(${parallaxImg})` }"></div>
            <div class="parallax-overlay"></div>
            <div class="parallax-caption">
                <span class="parallax-eyebrow">{{ $t('about.page.parallax_eyebrow') }}</span>
            </div>
        </section>

        <!-- BEGINNING -->
        <section id="beginning" class="block-light">
            <v-container class="block-container">
                <div class="section-meta">
                    <span class="chapter-num">02</span>
                    <span class="chapter-divider"></span>
                    <span class="chapter-eyebrow">{{ $t('about.page.beginning_eyebrow') }}</span>
                </div>

                <div class="section-grid">
                    <div class="section-headline">
                        <h2 class="section-title">{{ $t('about.beginning.title') }}</h2>
                    </div>
                    <div class="section-body">
                        <p class="section-text">{{ $t('about.beginning.line_1') }}</p>
                        <p class="section-text">{{ $t('about.beginning.line_2') }}</p>
                    </div>
                </div>

                <div class="beginning-photo">
                    <img src="@/assets/img/components/about/beginning2.webp" alt="" loading="lazy" decoding="async" />
                </div>
            </v-container>
        </section>

        <!-- SUCCESS -->
        <section id="success" class="block-dark">
            <div class="success-bg">
                <img src="@/assets/img/components/about/success.webp" alt="" loading="lazy" decoding="async" />
                <div class="success-overlay"></div>
            </div>

            <v-container class="block-container success-container">
                <div class="section-meta section-meta--light">
                    <span class="chapter-num">03</span>
                    <span class="chapter-divider"></span>
                    <span class="chapter-eyebrow">{{ $t('about.page.success_eyebrow') }}</span>
                </div>

                <div class="section-grid">
                    <div class="section-headline">
                        <h2 class="section-title section-title--light">{{ $t('about.success.title') }}.</h2>
                    </div>
                    <div class="section-body">
                        <p class="section-text section-text--light" v-html="$t('about.success.line_1')" />
                    </div>
                </div>
            </v-container>
        </section>
    </div>
</template>

<script>
import OptimizedImage from '@/components/common/OptimizedImage.vue'

export default {
    name: "ContentComponent",
    components: { OptimizedImage },
    data() {
        return {
            heroWebp: require('@/assets/img/components/about/team2.webp'),
            heroAvif: require('@/assets/img/components/about/team2.avif'),
            parallaxImg: require('@/assets/img/components/about/team2.webp')
        }
    }
}
</script>

<style lang="sass" scoped>
    #about
        background: #faf7f1
        color: #0a0828
        font-family: Inter, sans-serif

        ::v-deep .title-accent
            font-style: italic
            font-weight: 300

    /* === HERO === */
    .about-hero
        position: relative
        width: 100%
        height: 100vh
        min-height: 720px
        overflow: hidden
        background: #0a0828
        color: #fff
        display: flex
        flex-direction: column
        justify-content: space-between
        padding: 110px 0 50px

    .hero-media
        position: absolute
        inset: 0
        z-index: 0

        ::v-deep .optimized-image
            width: 100%
            height: 100%

        .hero-image
            width: 100%
            height: 100%
            object-fit: cover
            filter: blur(2px) saturate(1.1) brightness(0.7) contrast(1.05)
            transform: scale(1.05)

        @media (max-width: 960px)
            .hero-image
                transform: none
                filter: blur(1px) saturate(1.1) brightness(0.7) contrast(1.05)

        .hero-grain
            position: absolute
            inset: 0
            opacity: 0.18
            mix-blend-mode: overlay
            pointer-events: none
            background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>")

        .hero-overlay
            position: absolute
            inset: 0
            background: linear-gradient(180deg, rgba(10, 8, 40, 0.45) 0%, rgba(10, 8, 40, 0.25) 35%, rgba(10, 8, 40, 0.85) 100%)

    .hero-top
        position: relative
        z-index: 2
        display: flex
        align-items: center
        gap: 18px
        padding: 0 56px
        font-family: Inter, sans-serif
        font-size: 12px
        font-weight: 500
        letter-spacing: 0.18em
        text-transform: uppercase
        color: rgba(255, 255, 255, 0.85)

        .hero-divider
            width: 28px
            height: 1px
            background: rgba(255, 255, 255, 0.5)

    .hero-content
        position: relative
        z-index: 2
        max-width: 1280px
        margin: 0 auto
        padding: 0 56px
        width: 100%

        .hero-eyebrow
            display: inline-block
            font-family: Inter, sans-serif
            font-size: 12px
            font-weight: 500
            letter-spacing: 0.24em
            text-transform: uppercase
            color: rgba(255, 255, 255, 0.7)
            margin-bottom: 28px

        .hero-title
            font-family: Raleway, sans-serif
            font-weight: 900
            font-size: clamp(56px, 9vw, 132px)
            line-height: 0.95
            letter-spacing: -0.025em
            margin: 0 0 32px
            text-transform: lowercase
            color: #fff

        .hero-lead
            font-family: Inter, sans-serif
            font-size: clamp(16px, 1.5vw, 19px)
            line-height: 1.6
            font-weight: 400
            color: rgba(255, 255, 255, 0.88)
            max-width: 680px
            margin: 0
            text-indent: 0

    .hero-bottom
        position: relative
        z-index: 2
        display: flex
        justify-content: flex-end
        padding: 0 56px
        max-width: 1280px
        margin: 0 auto
        width: 100%

        .scroll-link
            display: inline-flex
            align-items: center
            gap: 12px
            font-family: Inter, sans-serif
            font-size: 12px
            font-weight: 500
            letter-spacing: 0.18em
            text-transform: uppercase
            color: rgba(255, 255, 255, 0.85)
            text-decoration: none
            padding: 14px 22px
            border: 1px solid rgba(255, 255, 255, 0.4)
            border-radius: 999px
            transition: all 0.2s ease

            &:hover
                background: rgba(255, 255, 255, 0.08)
                border-color: rgba(255, 255, 255, 0.7)

            .scroll-arrow
                font-size: 16px

    /* === BLOCK BASE === */
    .block-light
        background: #faf7f1
        color: #0a0828
        padding: 140px 0

    .block-dark
        position: relative
        background: #0a0828
        color: #fff
        padding: 160px 0
        overflow: hidden

    .block-container
        max-width: 1280px !important
        padding: 0 56px !important

    .section-meta
        display: flex
        align-items: center
        gap: 18px
        margin-bottom: 48px
        font-family: Inter, sans-serif
        font-size: 12px
        font-weight: 500
        letter-spacing: 0.22em
        text-transform: uppercase
        color: #0a0828

        .chapter-num
            font-family: Raleway, sans-serif
            font-weight: 700
            font-size: 14px
            letter-spacing: 0.1em
            color: #0a0828

        .chapter-divider
            width: 36px
            height: 1px
            background: #0a0828
            opacity: 0.4

        .chapter-eyebrow
            opacity: 0.85

        &.section-meta--light
            color: rgba(255, 255, 255, 0.92)

            .chapter-num
                color: #fff

            .chapter-divider
                background: #fff
                opacity: 0.5

    .section-grid
        display: grid
        grid-template-columns: 5fr 7fr
        gap: 80px

        .section-headline
            position: sticky
            top: 100px
            align-self: start

        .section-title
            font-family: Raleway, sans-serif
            font-weight: 900
            font-size: clamp(40px, 5vw, 72px)
            line-height: 1
            letter-spacing: -0.02em
            margin: 0
            color: #0a0828
            text-transform: lowercase

            &.section-title--light
                color: #fff

        .section-body
            display: flex
            flex-direction: column
            gap: 26px

            .section-text
                font-family: Inter, sans-serif
                font-size: clamp(15px, 1.1vw, 17px)
                line-height: 1.75
                font-weight: 400
                color: rgba(10, 8, 40, 0.82)
                margin: 0
                text-indent: 0

                &.section-text--light
                    color: rgba(255, 255, 255, 0.85)

    .ghost-cta
        display: inline-flex
        align-items: center
        gap: 12px
        margin-top: 16px
        align-self: flex-start
        font-family: Inter, sans-serif
        font-size: 13px
        font-weight: 500
        letter-spacing: 0.18em
        text-transform: uppercase
        color: #0a0828
        text-decoration: none
        padding: 16px 26px
        border: 1px solid rgba(10, 8, 40, 0.3)
        border-radius: 999px
        transition: all 0.2s ease

        &:hover
            background: #0a0828
            color: #fff
            border-color: #0a0828

            .ghost-cta-arrow
                transform: translateY(2px)

        .ghost-cta-arrow
            transition: transform 0.2s ease
            font-size: 16px

    /* === PARALLAX BREAK === */
    .parallax-break
        position: relative
        height: 60vh
        min-height: 420px
        overflow: hidden
        background: #0a0828

        .parallax-image
            position: absolute
            inset: -10%
            background-size: cover
            background-position: center
            background-attachment: fixed
            filter: saturate(1.05) brightness(0.85)

        .parallax-overlay
            position: absolute
            inset: 0
            background: linear-gradient(180deg, rgba(10, 8, 40, 0.35) 0%, rgba(10, 8, 40, 0.55) 100%)

        .parallax-caption
            position: absolute
            left: 0
            right: 0
            bottom: 36px
            text-align: center
            z-index: 1

            .parallax-eyebrow
                font-family: Inter, sans-serif
                font-size: 11px
                font-weight: 500
                letter-spacing: 0.28em
                text-transform: uppercase
                color: rgba(255, 255, 255, 0.85)

    /* === BEGINNING === */
    .beginning-photo
        margin-top: 100px
        position: relative
        max-width: min(1180px, 100%)
        margin-left: auto
        margin-right: auto
        overflow: hidden

        img
            width: 100%
            max-width: 100%
            height: auto
            display: block
            border-radius: 4px

        &::before
            content: ''
            position: absolute
            inset: -16px
            border: 1px solid rgba(10, 8, 40, 0.15)
            pointer-events: none

    /* === SUCCESS === */
    .block-dark .success-bg
        position: absolute
        inset: 0
        z-index: 0

        img
            width: 100%
            height: 100%
            object-fit: cover
            opacity: 0.35
            filter: saturate(1.1)

        .success-overlay
            position: absolute
            inset: 0
            background: linear-gradient(180deg, rgba(10, 8, 40, 0.78) 0%, rgba(10, 8, 40, 0.92) 100%)

    .success-container
        position: relative
        z-index: 1

    /* === RESPONSIVE === */
    @media (max-width: 960px)
        .about-hero
            min-height: 640px
            padding: 100px 0 30px

        .hero-top
            padding: 0 28px
            font-size: 11px
            gap: 14px

            .hero-divider--hide
            .hero-since--hide
                display: none

        .hero-content
            padding: 0 28px

            .hero-title
                font-size: clamp(48px, 12vw, 76px)

        .hero-bottom
            padding: 0 28px
            justify-content: center

            .scroll-link
                font-size: 11px
                padding: 12px 18px

        .block-light
            padding: 90px 0

        .block-dark
            padding: 110px 0

        .block-container
            padding: 0 28px !important

        .section-grid
            grid-template-columns: 1fr
            gap: 36px

            .section-headline
                position: static

        .parallax-break
            height: 40vh
            min-height: 280px

            .parallax-image
                background-attachment: scroll

        .beginning-photo
            margin-top: 60px

            &::before
                inset: -8px

    @media (max-width: 600px)
        .about-hero
            min-height: 560px
</style>
